import * as React from 'react'
import { useMemo } from 'react'

import { EmailLineOverviewProps, ExtendedEmailOverviewDto, useItemStyles } from './FileEmailOverview'
import { useOverviewStyles } from '../../../../pages/BaseOverview/BaseOverview'
import PersonAvatar from '../../../../components/PersonAvatar'
import FormattedDateTime from '../../../../components/Date/FormattedDateTime'
import { useTranslation } from 'react-i18next'
import { Button, Checkbox, mergeClasses, Text, Tooltip } from '@fluentui/react-components'
import { Attach20Regular, bundleIcon, EyeFilled, EyeRegular, OpenFilled, OpenRegular } from '@fluentui/react-icons'
import { useApplicationStyles } from '../../../../pages/App'
import { Recipient } from '@microsoft/microsoft-graph-types'
import useUser from '../../../../hooks/useUser'
import LoadingIcon from '../../../../components/Icon/LoadingIcon'
import useOpenDownloadMessage from '../../../hooks/useOpenDownloadMessage'

const PreviewIcon = bundleIcon(EyeFilled, EyeRegular)
const OpenIcon = bundleIcon(OpenFilled, OpenRegular)

const getEmailOtherDomain = (email: ExtendedEmailOverviewDto, currentMailbox: string): Recipient => {
    const currentDomain = currentMailbox?.substring(currentMailbox?.indexOf('@'))
    const senderDomain = email.sender?.emailAddress?.address?.substring(email.sender?.emailAddress?.address?.indexOf('@'))
    const recipient =
        email.toRecipients?.find(
            recipient => recipient.emailAddress.address?.substring(recipient.emailAddress.address.indexOf('@')).localeCompare(currentDomain) !== 0,
        ) || email.toRecipients?.[0]

    return senderDomain?.localeCompare(currentDomain) === 0 ? recipient : email.sender
}

export default function FileEmailListItem(props: EmailLineOverviewProps) {
    const appStyles = useApplicationStyles()
    const itemStyles = useItemStyles()
    const overviewStyles = useOverviewStyles()
    const { currentMailbox } = useUser()
    const { t: translate } = useTranslation()

    const virtualSender: Recipient = useMemo(() => getEmailOtherDomain(props.item, currentMailbox), [props.item])
    const { isOpening, openOrDownload } = useOpenDownloadMessage(props.item)

    return (
        <>
            {props.onItemSelected && (
                <Checkbox
                    className={overviewStyles.centerItems}
                    checked={props.isItemSelected?.(props.item)}
                    disabled={!props.isItemSelectable?.(props.item)}
                    onChange={(_, data) => props.onItemSelected(data, props.item)}
                />
            )}
            <div className={mergeClasses(appStyles.flexColumn, appStyles.gapS, overviewStyles.centerItems)}>
                <PersonAvatar name={virtualSender?.emailAddress?.name || translate('base.unknown')} color={'colorful'} size={24} />
                {props.item.hasAttachments ? (
                    <div>
                        <Attach20Regular />
                    </div>
                ) : (
                    <div></div>
                )}
            </div>
            <div className={itemStyles.listEmailDetails}>
                <Text className={appStyles.textNoWrapOverflowHidden}>{virtualSender?.emailAddress?.name || translate('base.unknown')}</Text>
                <Text className={appStyles.textNoWrapOverflowHidden}>{props.item.subject}</Text>
                {!props.viewportLarge && (
                    <Text size={200} className={appStyles.textNoWrapOverflowHidden}>
                        {props.item.bodyPreview?.substring(0, 100)}
                    </Text>
                )}
            </div>
            <div className={overviewStyles.alignEnd}>
                <div className={overviewStyles.alignEnd}>
                    <Tooltip relationship={'label'} withArrow content={translate('file.pivot.email.preview.button')}>
                        <Button
                            appearance="subtle"
                            icon={<PreviewIcon />}
                            onClick={e => {
                                e.stopPropagation()
                                props.onPreviewClick?.(props.item)
                            }}
                        />
                    </Tooltip>
                    <Tooltip relationship={'label'} withArrow content={translate('file.pivot.email.open.button')}>
                        <Button
                            appearance="subtle"
                            disabled={isOpening}
                            icon={isOpening ? <LoadingIcon /> : <OpenIcon />}
                            onClick={e => {
                                e.stopPropagation()
                                openOrDownload()
                            }}
                        />
                    </Tooltip>
                </div>
                <div>{props.item.sentDateTime && <FormattedDateTime date={props.item.sentDateTime} />}</div>
            </div>
        </>
    )
}
